import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * SVG circular gauge used by the Local Potential score on the Overview page.
 * Renders a track ring + foreground arc whose stroke length is derived from
 * (value / max). The foreground stroke uses a gradient defined inline.
 */
const CircularProgress = React.forwardRef(
	({ className, value = 0, max = 100, size = 160, strokeWidth = 12, label, sublabel, ...props }, ref) => {
		const radius = (size - strokeWidth) / 2;
		const circumference = 2 * Math.PI * radius;
		const pct = Math.min(max, Math.max(0, value)) / max;
		const dash = pct * circumference;
		const gradientId = React.useId();

		return (
			<div
				ref={ref}
				className={cn('relative inline-flex items-center justify-center', className)}
				style={{ width: size, height: size }}
				{...props}
			>
				<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
					<defs>
						<linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="hsl(var(--brand-violet))" />
							<stop offset="50%" stopColor="hsl(var(--brand-magenta))" />
							<stop offset="100%" stopColor="hsl(var(--brand-orange))" />
						</linearGradient>
					</defs>
					<circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--secondary))" strokeWidth={strokeWidth} />
					<circle
						cx={size / 2}
						cy={size / 2}
						r={radius}
						fill="none"
						stroke={`url(#${gradientId})`}
						strokeWidth={strokeWidth}
						strokeLinecap="round"
						strokeDasharray={`${dash} ${circumference}`}
						className="transition-[stroke-dasharray] duration-700 ease-out"
					/>
				</svg>
				<div className="absolute inset-0 flex flex-col items-center justify-center">
					{label !== undefined && label !== null ? (
						typeof label === 'string' || typeof label === 'number' ? (
							<div className="text-3xl font-semibold tabular-nums text-foreground">{label}</div>
						) : (
							label
						)
					) : null}
					{sublabel ? <div className="text-xs uppercase tracking-wide text-muted-foreground">{sublabel}</div> : null}
				</div>
			</div>
		);
	}
);
CircularProgress.displayName = 'CircularProgress';

export { CircularProgress };
